import React, { useContext, useReducer, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Store } from '../../Store';
import { getError } from '../../utils';
import LoadingBox from '../../components/LoadingBox';
import { useTranslation } from 'react-i18next';
import userService from '../../services/user.service';
import ConfirmModal from '../../components/ConfirmModal';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import PageHeader from '../../components/shared/PageHeader';
import { USER_ROLE, flagsFromUserRole } from '../../utils/userRole';

const reducer = (state, action) => {
  switch (action.type) {
    case 'CREATE_REQUEST':
      return { ...state, loadingCreate: true };
    case 'CREATE_SUCCESS':
      return {
        ...state,
        loadingCreate: false,
      };
    case 'CREATE_FAIL':
      return { ...state, loadingCreate: false };
    default:
      return state;
  }
};

function NewUser() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useContext(Store);
  const { userInfo } = state;
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState();
  const [userRole, setUserRole] = useState(USER_ROLE.CLIENT);

  const [showModal, setShowModal] = useState(false);
  const handleClose = () => setShowModal(false);
  const handleShow = () => setShowModal(true);

  const [{ loadingCreate }, dispatch] = useReducer(reducer, {
    loadingCreate: false,
  });

  const handleSubmit = event => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      event.preventDefault();
      event.stopPropagation();
      handleShow();
    }
  };

  const submitHandler = async e => {
    e.preventDefault();
    handleClose();
    dispatch({ type: 'CREATE_REQUEST' });
    try {
      const { isAdmin, isSuperAdmin, canVerify } = flagsFromUserRole(userRole);
      await userService.createUser(
        {
          nombre,
          email,
          telefono,
          isAdmin,
          isSuperAdmin,
          canVerify,
        },
        userInfo.token
      );
      dispatch({ type: 'CREATE_SUCCESS' });
      toast.success(t('user.userCreated'));
      navigate('/AdminScreen/users');
    } catch (err) {
      toast.error(getError(err));
      dispatch({
        type: 'CREATE_FAIL',
      });
    }
  };

  return (
    <div className='max-w-2xl mx-auto space-y-6 p-6'>
      {loadingCreate && <LoadingBox overlay={true} />}
      <ConfirmModal
        show={showModal}
        title={t('user.newUser')}
        message={t('user.confirmCreateUser')}
        onConfirm={submitHandler}
        onCancel={handleClose}
        confirmLabel={t('user.createUser')}
        cancelLabel={t('common.cancel')}
      />
      <Helmet>
        <title>{t('user.newUser')}</title>
      </Helmet>

      <PageHeader
        title={t('user.newUser')}
        actions={
          <Button variant='outline' size='sm' onClick={() => navigate('/AdminScreen/users')}>
            <ArrowLeft className='h-4 w-4' />
            {t('common.back')}
          </Button>
        }
      />

      <Card>
        <CardContent className='pt-6'>
          <form noValidate onSubmit={handleSubmit} className='space-y-4'>
            <div className='space-y-1'>
              <label htmlFor='nombre' className='text-sm font-medium text-foreground'>
                {t('auth.name')}
              </label>
              <input
                id='nombre'
                type='text'
                required
                onChange={e => setNombre(e.target.value)}
                className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
              />
            </div>

            <div className='space-y-1'>
              <label htmlFor='email' className='text-sm font-medium text-foreground'>
                {t('auth.email')}
              </label>
              <input
                id='email'
                type='email'
                required
                onChange={e => setEmail(e.target.value.toLowerCase())}
                className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
              />
            </div>

            <div className='space-y-1'>
              <label htmlFor='telefono' className='text-sm font-medium text-foreground'>
                {t('user.phone')}
              </label>
              <input
                id='telefono'
                type='number'
                required
                onChange={e => setTelefono(e.target.value)}
                className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
              />
            </div>

            <div className='space-y-1'>
              <label htmlFor='userRole' className='text-sm font-medium text-foreground'>
                {t('user.role')}
              </label>
              <select
                id='userRole'
                value={userRole}
                onChange={e => setUserRole(e.target.value)}
                disabled={!userInfo.isSuperAdmin}
                className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60'
              >
                <option value={USER_ROLE.CLIENT}>{t('user.roleClient')}</option>
                <option value={USER_ROLE.ADMIN}>{t('user.roleAdmin')}</option>
                <option value={USER_ROLE.ADMIN_VERIFY}>{t('user.roleAdminVerify')}</option>
                <option value={USER_ROLE.SUPER_ADMIN}>{t('user.roleSuperAdmin')}</option>
              </select>
            </div>

            <Button type='submit' className='w-full'>
              {t('user.createUser')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default NewUser;
